import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlarmClock,
  Archive,
  CheckCircle2,
  Inbox,
  LifeBuoy,
  Plus,
  Search,
  UserPlus,
} from "lucide-react";
import * as React from "react";
import { useNavigate } from "react-router-dom";

import { ApiError } from "@/api/client";
import { tickets } from "@/api/endpoints";
import { useAuth } from "@/auth/AuthProvider";
import { useDebounced } from "@/hooks/useDebounced";
import { FilterBar, PageHeader, Pagination } from "@/components/PageHeader";
import { Badge, toneFor } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { StatTile } from "@/components/charts";
import {
  EmptyState,
  ErrorState,
  FormError,
  Skeleton,
  Spinner,
  TableSkeleton,
} from "@/components/ui/feedback";
import { Input, Label, Textarea } from "@/components/ui/input";
import { Select, ToggleGroup } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { humanize, relativeTime } from "@/lib/utils";

const STATUS_OPTIONS = [
  { label: "Open", value: "OPEN" },
  { label: "In progress", value: "IN_PROGRESS" },
  { label: "Awaiting customer", value: "PENDING_CUSTOMER" },
  { label: "Resolved", value: "RESOLVED" },
  { label: "Closed", value: "CLOSED" },
];

const PRIORITY_OPTIONS = [
  { label: "Urgent", value: "URGENT" },
  { label: "High", value: "HIGH" },
  { label: "Normal", value: "NORMAL" },
  { label: "Low", value: "LOW" },
];

/** Priority always ships with its label beside it — colour is reinforcement. */
const PRIORITY_COLOR: Record<string, string> = {
  URGENT: "var(--status-critical)",
  HIGH: "var(--status-serious)",
  NORMAL: "var(--series-2)",
  LOW: "var(--chart-axis)",
};

const VIEW_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Unassigned", value: "unassigned" },
  { label: "Mine", value: "mine" },
  { label: "Past SLA", value: "overdue" },
];

export default function Tickets() {
  const { can, me } = useAuth();
  const navigate = useNavigate();

  const [page, setPage] = React.useState(1);
  const [view, setView] = React.useState("all");
  const [status, setStatus] = React.useState("");
  const [priority, setPriority] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [creating, setCreating] = React.useState(false);

  const q = useDebounced(search);

  React.useEffect(() => setPage(1), [view, status, priority, q]);

  const query = useQuery({
    queryKey: ["tickets", { page, view, status, priority, q }],
    queryFn: () =>
      tickets.list({
        page,
        page_size: 20,
        ticket_status: status || undefined,
        priority: priority || undefined,
        unassigned: view === "unassigned" || undefined,
        overdue: view === "overdue" || undefined,
        assignee_staff_id: view === "mine" ? me?.id : undefined,
        q: q || undefined,
      }),
  });

  return (
    <>
      <PageHeader
        title="Support tickets"
        description="Issues raised by farmers and providers, and everything your team has logged."
        actions={
          can("ticket.write") && (
            <Button onClick={() => setCreating(true)}>
              <Plus />
              New ticket
            </Button>
          )
        }
      />

      <TicketStatTiles />

      <FilterBar>
        <ToggleGroup value={view} onValueChange={setView} options={VIEW_OPTIONS} />
        <div className="relative min-w-[13rem] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search subject, reference or contact"
            className="pl-8"
            aria-label="Search tickets"
          />
        </div>
        <div className="w-40">
          <Select
            value={status}
            onValueChange={setStatus}
            options={STATUS_OPTIONS}
            placeholder="Any status"
            aria-label="Filter by status"
          />
        </div>
        <div className="w-36">
          <Select
            value={priority}
            onValueChange={setPriority}
            options={PRIORITY_OPTIONS}
            placeholder="Any priority"
            aria-label="Filter by priority"
          />
        </div>
      </FilterBar>

      <Card className="overflow-hidden">
        {query.isLoading ? (
          <TableSkeleton cols={6} />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        ) : query.data && query.data.items.length > 0 ? (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reference</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>SLA</TableHead>
                  <TableHead>Requester</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {query.data.items.map((t) => (
                  <TableRow
                    key={t.id}
                    onClick={() => navigate(`/tickets/${t.id}`)}
                    className="cursor-pointer"
                  >
                    <TableCell className="tabular whitespace-nowrap text-muted-foreground">
                      <span className="flex items-center gap-2">
                        {/* Priority stripe — reinforces the badge, never the
                            only carrier of the value. */}
                        <span
                          aria-hidden
                          className="h-5 w-1 shrink-0 rounded-full"
                          style={{ background: PRIORITY_COLOR[t.priority] }}
                        />
                        {t.reference}
                      </span>
                    </TableCell>
                    <TableCell className="max-w-[24rem] truncate font-medium">
                      {t.subject}
                    </TableCell>
                    <TableCell>
                      <Badge variant={toneFor(t.status)}>{humanize(t.status)}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={toneFor(t.priority)}>
                        {humanize(t.priority)}
                      </Badge>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs">
                      {t.is_sla_breached ? (
                        <span
                          className="font-medium"
                          style={{ color: "var(--status-critical)" }}
                        >
                          Overdue {relativeTime(t.sla_due_at)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">
                          {t.sla_due_at ? `Due ${relativeTime(t.sla_due_at)}` : "—"}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="tabular text-muted-foreground">
                      {t.requester_contact ?? "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pagination
              page={query.data.page}
              pageSize={query.data.pageSize}
              total={query.data.total}
              onPageChange={setPage}
            />
          </>
        ) : (
          <EmptyState
            icon={LifeBuoy}
            title="No tickets in this view"
            description="Try a different filter, or log a new ticket when a customer calls."
          />
        )}
      </Card>

      {creating && <CreateTicketDialog onClose={() => setCreating(false)} />}
    </>
  );
}

/** Colourful at-a-glance row above the queue. */
function TicketStatTiles() {
  const stats = useQuery({ queryKey: ["ticket-stats"], queryFn: tickets.stats });

  if (stats.isLoading) {
    return (
      <div className="mb-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-[92px]" />
        ))}
      </div>
    );
  }
  if (!stats.data) return null;

  const s = stats.data;
  const active =
    s.by_status.OPEN + s.by_status.IN_PROGRESS + s.by_status.PENDING_CUSTOMER;

  return (
    <div className="mb-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
      <StatTile label="Active" value={active} hint="Not yet resolved" icon={Inbox} />
      <StatTile
        label="Unassigned"
        value={s.unassigned}
        hint={s.unassigned > 0 ? "Nobody owns these" : "All owned"}
        tone={s.unassigned > 0 ? "warning" : "good"}
        icon={UserPlus}
      />
      <StatTile
        label="Past SLA"
        value={s.overdue}
        hint={s.overdue > 0 ? "Breached target" : "All on time"}
        tone={s.overdue > 0 ? "critical" : "good"}
        icon={AlarmClock}
      />
      <StatTile
        label="Resolved"
        value={s.by_status.RESOLVED}
        hint="Awaiting close"
        tone="good"
        icon={CheckCircle2}
      />
      <StatTile
        label="All time"
        value={s.total}
        hint={`${s.by_status.CLOSED} closed`}
        icon={Archive}
      />
    </div>
  );
}

function CreateTicketDialog({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [subject, setSubject] = React.useState("");
  const [body, setBody] = React.useState("");
  const [contact, setContact] = React.useState("");
  const [priority, setPriority] = React.useState("NORMAL");
  const [channel, setChannel] = React.useState("PHONE");
  const [error, setError] = React.useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      tickets.create({
        subject: subject.trim(),
        body: body.trim(),
        priority,
        channel,
        requester_contact: contact.trim() || null,
      }),
    onSuccess: (ticket) => {
      toast.success("Ticket created", ticket.reference);
      void queryClient.invalidateQueries({ queryKey: ["tickets"] });
      void queryClient.invalidateQueries({ queryKey: ["ticket-stats"] });
      onClose();
      navigate(`/tickets/${ticket.id}`);
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : "Could not create the ticket."),
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New ticket</DialogTitle>
          <DialogDescription>
            Log an issue a customer has raised by phone, WhatsApp or email.
          </DialogDescription>
        </DialogHeader>

        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            create.mutate();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="subject">Subject</Label>
            <Input
              id="subject"
              required
              minLength={3}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. Provider did not arrive for the booked slot"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="body">What happened</Label>
            <Textarea
              id="body"
              required
              rows={4}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Describe the issue in the customer's words."
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="contact">Requester contact</Label>
              <Input
                id="contact"
                required
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                placeholder="+91…"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="channel">Channel</Label>
              <Select
                value={channel}
                onValueChange={setChannel}
                options={[
                  { label: "Phone", value: "PHONE" },
                  { label: "WhatsApp", value: "WHATSAPP" },
                  { label: "Email", value: "EMAIL" },
                  { label: "In app", value: "APP" },
                  { label: "Internal", value: "INTERNAL" },
                ]}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="priority">Priority</Label>
            <ToggleGroup
              value={priority}
              onValueChange={setPriority}
              options={PRIORITY_OPTIONS}
            />
            <p className="text-[11px] text-muted-foreground">
              Sets the SLA target: urgent 4h, high 12h, normal 48h, low 5 days.
            </p>
          </div>

          <FormError message={error} />

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending && <Spinner />}
              Create ticket
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
